import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ACCOUNT_TYPES = [
  { value: "business", label: "Geschäftskonto" },
  { value: "tax", label: "Steuerkonto" },
  { value: "reserve", label: "Rücklagenkonto" },
  { value: "paypal", label: "PayPal" },
  { value: "stripe", label: "Stripe" },
  { value: "wise", label: "Wise" },
  { value: "cash", label: "Bar/Kasse" },
  { value: "other", label: "Sonstiges" },
];

const empty = { name: "", type: "business", current_balance: 0, notes: "" };

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    const payload = { ...form, current_balance: Number(form.current_balance) || 0 };
    try {
      if (editing) await api.patch(`/accounts/${editing.id}`, payload);
      else await api.post("/accounts", payload);
      toast.success(editing ? "Konto aktualisiert" : "Konto angelegt");
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Konto wirklich löschen?")) return;
    await api.delete(`/accounts/${id}`);
    toast.success("Konto gelöscht");
    load();
  };

  const total = accounts.reduce((s, a) => s + (a.current_balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Konten</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Konten & Stände</h1>
          <p className="text-sm text-neutral-500 mt-2">Verwalte alle Bank- und Zahlungskonten deiner Agentur.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-account-btn"><Plus className="h-4 w-4 mr-2" />Neues Konto</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Konto bearbeiten" : "Neues Konto"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" /></div>
              <div>
                <Label>Typ</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="account-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Aktueller Stand (€)</Label><Input type="number" step="0.01" value={form.current_balance} onChange={(e) => setForm({ ...form, current_balance: e.target.value })} data-testid="account-balance" /></div>
              <div><Label>Notizen</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="account-notes" /></div>
            </div>
            <DialogFooter>
              <Button onClick={onSave} data-testid="account-save">{editing ? "Speichern" : "Anlegen"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-6">
          <Kpi label="Summe aller Konten" value={fmtCurrency(total)} />
          <Kpi label="Geschäftskonten" value={fmtCurrency(accounts.filter(a=>a.type==="business").reduce((s,a)=>s+a.current_balance,0))} />
          <Kpi label="Steuerkonto" value={fmtCurrency(accounts.filter(a=>a.type==="tax").reduce((s,a)=>s+a.current_balance,0))} />
          <Kpi label="Rücklage" value={fmtCurrency(accounts.filter(a=>a.type==="reserve").reduce((s,a)=>s+a.current_balance,0))} />
        </div>
        <Table data-testid="accounts-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead className="text-right">Aktueller Stand</TableHead>
              <TableHead>Letzte Aktualisierung</TableHead>
              <TableHead>Notiz</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-neutral-500">Lade…</TableCell></TableRow> :
              accounts.map((a) => (
                <TableRow key={a.id} className="table-row-hover" data-testid={`account-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>{ACCOUNT_TYPES.find(t => t.value === a.type)?.label || a.type}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(a.current_balance)}</TableCell>
                  <TableCell className="text-neutral-500 text-sm">{fmtDate(a.last_updated)}</TableCell>
                  <TableCell className="text-neutral-500 text-sm">{a.notes}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(a); setForm({ name: a.name, type: a.type, current_balance: a.current_balance, notes: a.notes || "" }); setOpen(true); }} data-testid={`account-edit-${a.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(a.id)} data-testid={`account-delete-${a.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold tracking-tight mt-1">{value}</div>
    </div>
  );
}
